import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Spin } from 'antd';

import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import timeGridPlugin from '@fullcalendar/timegrid';
import classnames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { hasPermission } from '../../helpers/user';

import { DATE_FORMAT } from '../../constants/common';
import { EVENT_MODAL_MODE, RELATION_SUBTYPE } from '../../constants/event';
import messages from '../../constants/messages';
import { PERMISSIONS } from '../../constants/permissions';
import { TASK_MODAL_MODE } from '../../constants/task';

import { useModal } from '../../hooks/useModal';

import { setCurrentSelectedDay } from '../../redux/actions/calendar';
import { setFilteredEvents } from '../../redux/actions/event';
import { getAllEvents, setCurrentEvent, setEventQueryParams, updateEvent } from '../../redux/actions/event';
import { setCurrentTask, updateTask } from '../../redux/actions/task';

import CustomAgenda from '../../components/Calendar/CustomAgenda';

import styles from './styles.module.scss';

import EventModal from '../Planner/EventModal';
import TaskModal from '../Planner/TaskModal';
import { successToastHandler, warnToastHandler } from '../action_notifier';
import CustomToolbar from './CustomToolbar';
import {
  CALENDAR_VIEWS,
  DEFAULT_CALENDAR_HEIGHT,
  DEFAULT_EVENT_BG_COLOR,
  DEFAULT_TASK_DURATION,
  DOUBLE_CLICK_DELAY,
  EVENT_BG_COLOR,
  EVENT_TYPE,
  TASK_BG_COLOR
} from './constants';

dayjs.extend(utc);

function adjustRemindDate(oldDueDate, newDueDate, remindDate) {
  const oldDue = dayjs(oldDueDate);
  const newDue = dayjs(newDueDate);
  const remind = dayjs(remindDate);

  const diffMs = oldDue.diff(remind);

  return newDue.subtract(diffMs, 'millisecond').toISOString();
}

const Calendar = ({
  calendarHeight = DEFAULT_CALENDAR_HEIGHT,
  calendarSizes,
  className,
  excludeEventList = true,
  initialView = CALENDAR_VIEWS.WEEK,
  isLoading = false,
  isToolbarVisible = true,
  queryParameters,
  slotDuration = '00:30:00',
  slotLabelFormat = { hour: '2-digit', minute: '2-digit', hour12: false }
}) => {
  const dispatch = useDispatch();

  const { currentSelectedDate } = useSelector(state => state.calendar);
  const { data: geofences } = useSelector(state => state.geofence);
  const { filteredEvents } = useSelector(state => state.event);
  const { filteredTasks } = useSelector(state => state.task);
  const { permissions } = useSelector(state => state.user);

  const [isOpenCreateEventModal, openCreateEventModal, closeCreateEventModal] = useModal();
  const [isOpenEventModal, openEventModal, closeEventModal] = useModal();
  const [isOpenTaskModal, openTaskModal, closeTaskModal] = useModal();

  const [currentView, setCurrentView] = useState(initialView);
  const [newEvent, setNewEvent] = useState({
    startDate: null,
    endDate: null
  });
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  const calendarRef = useRef();
  const lastSlotClickTimeRef = useRef(null);

  const getEvents = (startDate = dayjs().startOf('month'), endDate = dayjs().endOf('month')) => {
    const parsedDate = endDate.format(DATE_FORMAT.DATETIME);
    const params = {
      startDate: dayjs.utc(startDate.format(DATE_FORMAT.DATETIME)).toISOString(),
      endDate: dayjs.utc(parsedDate, DATE_FORMAT.DATETIME).toISOString(),
      ...queryParameters
    };

    dispatch(setEventQueryParams(params));
    dispatch(getAllEvents(params));
  };

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.changeView(currentView);
    }
  }, [currentView]);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.gotoDate(currentSelectedDate);
    }
  }, [currentSelectedDate]);

  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (calendarApi) {
      calendarApi.render();
    }
  }, [calendarSizes]);

  const resources = useMemo(
    () => geofences?.filter(g => !g.is_master)?.map(g => ({ id: g.id, title: g.name, eventColor: g.color })),
    [geofences]
  );

  const formatEvents = useCallback(
    (events, tasks) => {
      const calendarEvents = [
        ...events.map(event => {
          const autoEvents = event?.relations_with_subtypes?.filter(r => r.relation_subtype_field);
          const isRotationEvent = event?.relations_with_subtypes?.at(0)?.relation_subtype === RELATION_SUBTYPE.ROTATION;
          const diffInHours = dayjs(event.end_at).diff(dayjs(event.start_at), 'hour', true);
          const isAllDay = diffInHours >= 24;

          return {
            ...event,
            id: `event-${event.id}`,
            eventType: EVENT_TYPE.EVENT,
            title: event.title,
            start: dayjs(event.start_at).toDate(),
            end: dayjs(event.end_at).toDate(),
            backgroundColor: event.resource_id
              ? resources?.find(r => r.id === event.resource_id)?.eventColor
              : (EVENT_BG_COLOR[event?.relations_with_subtypes?.at(0)?.relation_subtype] ?? DEFAULT_EVENT_BG_COLOR),
            resourceId: event.resource_id,
            allDay: isRotationEvent || isAllDay || autoEvents?.length
          };
        }),
        ...tasks.map(task => ({
          ...task,
          id: `task-${task.id}`,
          eventType: EVENT_TYPE.TASK,
          title: task.title,
          start: dayjs(task.due_date).subtract(DEFAULT_TASK_DURATION, 'minutes').toDate(),
          end: dayjs(task.due_date).toDate(),
          backgroundColor: TASK_BG_COLOR,
          durationEditable: false
        }))
      ];

      if (newEvent.startDate) {
        calendarEvents.push({
          title: '(No title)',
          start: newEvent.startDate,
          end: newEvent.endDate,
          eventType: EVENT_TYPE.EVENT,
          backgroundColor: DEFAULT_EVENT_BG_COLOR
        });
      }

      return calendarEvents;
    },
    [newEvent, geofences]
  );

  const parseCalendarEventId = useCallback(id => (id || '').split('-').at(-1), []);

  const onView = view => {
    closeCreateEventModal();
    closeEventModal();
    closeTaskModal();
    setCurrentView(view);
  };

  const onNavigate = date => {
    const startDate = dayjs(date).startOf('year');
    const endDate = dayjs(date).endOf('year');

    getEvents(startDate, endDate);
  };

  const onCreateEventSubmit = () => {
    setNewEvent({ startDate: null, endDate: null });
    closeCreateEventModal();
  };

  const onCancelCreateEvent = () => {
    closeCreateEventModal();
    setNewEvent({ startDate: null, endDate: null });
  };

  const onSelectEvent = ({ event }) => {
    const formattedEvent = { ...event.extendedProps, id: parseCalendarEventId(event.id) };

    delete formattedEvent.titleWithTime;
    delete formattedEvent.end;
    delete formattedEvent.start;

    if (formattedEvent.eventType === EVENT_TYPE.EVENT) {
      dispatch(setCurrentEvent(formattedEvent));
      openEventModal();
    } else if (formattedEvent.eventType === EVENT_TYPE.TASK) {
      dispatch(setCurrentTask(formattedEvent));
      openTaskModal();
    }
  };

  const parseDuration = durationStr => {
    const [hours, minutes, seconds] = durationStr.split(':').map(Number);

    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  };

  const isSingleSlotSelected = slot => {
    const durationMs = parseDuration(slotDuration);

    const start = slot.start.getTime();
    const end = slot.end.getTime();

    switch (currentView) {
      case CALENDAR_VIEWS.MONTH:
      case CALENDAR_VIEWS.YEAR:
        return end - start === 24 * 60 * 60 * 1000;

      default:
        return end - start <= durationMs;
    }
  };

  const onSelectSlot = async slot => {
    const isSingleSlot = isSingleSlotSelected(slot);
    const now = Date.now();

    if (isSingleSlot && now - lastSlotClickTimeRef.current < DOUBLE_CLICK_DELAY) {
      lastSlotClickTimeRef.current = null;
      setSelectedCalendarDate(null);
    } else if (isSingleSlot) {
      lastSlotClickTimeRef.current = now;
      setSelectedCalendarDate(slot.startStr);

      return;
    }

    if (!hasPermission([PERMISSIONS.EVENTS.CREATE], permissions)) {
      return;
    }

    setNewEvent({ startDate: slot.start, endDate: slot.end });
    openCreateEventModal();
  };

  async function updateCurrentEvent({ event, revert }) {
    if (event.start < new Date()) {
      revert();
      warnToastHandler(messages.CANT_MOVE_AN_EVENT_INTO_THE_PAST);

      return;
    }

    try {
      const newEvent = Object.assign({}, event.extendedProps, {
        id: parseCalendarEventId(event.id),
        startDate: event.start?.toISOString(),
        endDate: event.end?.toISOString(),
        title: event.title
      });

      await dispatch(updateEvent(newEvent.id, newEvent)).then(updatedItem => {
        // need to update the filteredEvents to show updated data in Calendar
        const updatedEvent = filteredEvents.map(item => {
          if (item.id === updatedItem.id) {
            return {
              ...item,
              ...updatedItem
            };
          }
          return item;
        });
        dispatch(setFilteredEvents(updatedEvent));
      });

      successToastHandler(messages.UPDATED);
    } catch (error) {
      revert();
      throw error;
    }
  }

  async function updateCurrentTask({ event, revert }) {
    if (event.start < new Date()) {
      revert();
      warnToastHandler(messages.CANT_MOVE_AN_TASK_INTO_THE_PAST);

      return;
    }

    try {
      const updatedTask = Object.assign({}, event.extendedProps, {
        id: parseCalendarEventId(event.id),
        dueDate: event.end?.toISOString(),
        remindDate: event.extendedProps.remind_at
          ? adjustRemindDate(event.extendedProps.due_date, event.end?.toISOString(), event.extendedProps.remind_at)
          : null,
        title: event.title
      });

      await dispatch(updateTask(updatedTask.id, updatedTask));
      successToastHandler(messages.UPDATED);
    } catch (error) {
      revert();
      throw error;
    }
  }

  const moveEvent = async ({ event, revert }) => {
    if (event.extendedProps.eventType === EVENT_TYPE.EVENT) {
      await updateCurrentEvent({ event, revert });
    }

    if (event.extendedProps.eventType === EVENT_TYPE.TASK) {
      await updateCurrentTask({ event, revert });
    }
  };

  const getDayHeaderFormat = () => {
    switch (currentView) {
      case CALENDAR_VIEWS.DAY:
      case CALENDAR_VIEWS.WEEK:
        return { weekday: 'short', day: '2-digit', month: undefined };

      default:
        return { weekday: 'short' };
    }
  };

  const handleViewChange = view => {
    setCurrentView(view);
    setSelectedCalendarDate(null);
  };

  const handleNavLinkDayClick = date => {
    setCurrentView(CALENDAR_VIEWS.DAY);
    dispatch(setCurrentSelectedDay(date.toISOString()));
  };

  return (
    <div className={classnames(styles['calendar-component'], className)}>
      {isToolbarVisible && (
        <CustomToolbar
          currentView={currentView}
          excludeEventList={excludeEventList}
          selectedCalendarDate={selectedCalendarDate}
          handleViewChange={handleViewChange}
          onNavigate={onNavigate}
          onView={onView}
        />
      )}

      <div className="position-relative">
        {currentView === CALENDAR_VIEWS.LIST ? (
          <CustomAgenda events={formatEvents(filteredEvents, [])} onView={onView} />
        ) : (
          <div style={{ position: 'relative' }}>
            {isLoading && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255,255,255,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 999
                }}>
                <Spin tip="Loading events..." />
              </div>
            )}
            <FullCalendar
              dayHeaderFormat={getDayHeaderFormat()}
              droppable={true}
              editable={true}
              headerToolbar={false}
              navLinks={true}
              nowIndicator={true}
              selectable={true}
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              slotLabelFormat={slotLabelFormat}
              events={formatEvents(filteredEvents, filteredTasks)}
              height={calendarHeight}
              initialView={currentView}
              plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin, multiMonthPlugin, resourceTimelinePlugin]}
              ref={calendarRef}
              scrollTime="05:00:00"
              slotDuration={slotDuration}
              eventClick={onSelectEvent}
              eventDrop={moveEvent}
              eventResize={moveEvent}
              select={onSelectSlot}
              navLinkDayClick={date => handleNavLinkDayClick(date)}
            />
          </div>
        )}
      </div>
      <EventModal
        endDate={newEvent.endDate}
        startDate={newEvent.startDate}
        isOpen={isOpenCreateEventModal}
        mode={EVENT_MODAL_MODE.CREATE}
        onCancel={onCancelCreateEvent}
        onSubmit={onCreateEventSubmit}
      />
      <EventModal isOpen={isOpenEventModal} onCancel={closeEventModal} mode={EVENT_MODAL_MODE.VIEW} />
      <TaskModal isOpen={isOpenTaskModal} mode={TASK_MODAL_MODE.VIEW} onCancel={closeTaskModal} />
    </div>
  );
};

export default memo(Calendar);
